<script setup lang="ts">
import {transactionService} from '~/api/service/transaction.service'
import {hexToUtf8} from '~/utils/decode.util'
import BcSelect from '~/ui/Select/BcSelect.vue'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import type { ValueOf } from '~/types/value-of';
import { VIEW_TYPE, VIEW_TYPE_LIST, type ViewType } from '~/constants/transaction.constants';

interface IProps {
  originalInputData: string
}

const props = defineProps<IProps>()

const defaultRows = 8

const txnHash = getRouteParam('txnHash')

const {data: abiData} = transactionService.useGetEvmAbiFunctionParamList({
  trxHash: txnHash
})

const viewType = ref<ViewType>(VIEW_TYPE.DEFAULT)
const showDecodeValue = ref(false)

const inputValue = computed(() => {
  if (showDecodeValue.value) {
    return abiData.value?.decodedParamsString
  }
  switch (viewType.value) {
    case 'utf-8':
      return hexToUtf8(props.originalInputData)
    case 'default':
    case null:
      return abiData.value?.formattedAbiString
    default:
      return props.originalInputData
  }
})
</script>

<template>
  <div class="relative flex flex-col gap-y-2">
    <textarea
      :value="inputValue"
      class="input-data"
      :rows="defaultRows"
      readonly
    />
    <div class="flex items-center gap-2 text-base mobile:flex-col mobile:items-start">
      <BcSelect
        v-if="!showDecodeValue"
        v-model="viewType"
        :options="VIEW_TYPE_LIST"
        class="bg-primary/10 py-2 px-2.5 hover:!bg-primary/20"
      />
      <button
        class="flex items-center gap-x-2 py-2 px-2.5 bg-primary/10 rounded-lg hover:bg-primary/20 disabled:opacity-40"
        @click.stop="showDecodeValue = !showDecodeValue"
        :disabled="!abiData?.isDecodable"
      >
        <FontAwesomeIcon :icon="showDecodeValue ? 'undo' : 'cube'"/>
        {{ showDecodeValue ? 'Switch Back' : 'Decode Input Data' }}
      </button>
    </div>
    <FontAwesomeIcon
      icon="expand-alt"
      class="icon"
    />
  </div>
</template>

<style lang="scss" scoped>
.input-data {
  @apply w-full py-4 pl-4 pr-12 text-lg leading-6 text-primary bg-primary/5 border-0 rounded-lg font-code resize-y min-h-[48px] font-normal outline-none;

  &::-webkit-resizer {
    @apply hidden;
  }

  &::-webkit-scrollbar {
    @apply border-0 w-2;
  }

  &::-webkit-scrollbar-thumb {
    @apply rounded-lg bg-primary/20;
  }

  &::-webkit-scrollbar-corner {
    @apply bg-transparent;
  }
}

.icon {
  @apply absolute bottom-12 right-1 text-primary/40 mobile:bottom-[92px] mobile:right-2 pointer-events-none;
}
</style>